/* eslint-disable no-nested-ternary */
import { CommonModule } from '@angular/common';
import {
  Component, EventEmitter, Inject, OnDestroy, OnInit, Output
} from '@angular/core';
import {
  FormControl, FormGroup, ReactiveFormsModule, Validators
} from '@angular/forms';
import { ActivatedRoute, ParamMap } from '@angular/router';

import {
  catchError,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs';

import { GtmDataLayerService } from '@sondermind/google-tag-manager';
import { IAPIFilterOption } from '@sondermind/utilities/models-http';
import {
  CASH_PAY_OPTION_VALUE,
  IInsurance,
  IInsuranceOptionValue,
  InsuranceCategoryName,
  InsuranceType,
  NoInsuranceOptionValue
} from '@sondermind/utilities/models-provider-network-company';
import { DropdownOption } from '@sondermind/iris/dropdown';
import { IState, StatesServedStore } from '@sondermind/data-access/public-facing/states-served';

import { LAUNCH_DARKLY_SERVICE, LDBooleanFeatureFlags, LaunchDarklyService } from '@sondermind/launch-darkly';
import {
  compareConcernValues,
  compareInsuranceValues,
  compareSimpleTypeOptionValues
} from '../../helpers/form-options-comparison-helpers';
import {
  IFilterForm, IFilterFormValues, ISeoElements, UrlQueryParams
} from '../../models/filter-form.interface';
import { IGender } from '../../models/gender';
import { IModality } from '../../models/modality';
import { IMoreFiltersSelections } from '../../models/more-filters.interface';
import { IOptionCategory } from '../../models/option-category.interface';
import { IConcernOptionValue, ISpecialty } from '../../models/specialty';
import { OutgoingFilterConversionService } from '../../services/outgoing-filter-conversion.service';
import { FilterOptionsStore } from '../../stores/filter-options.store';
import { FilterSelectModule } from '../filter-select/select.module';
import { MoreFiltersComponent } from '../more-filters/more-filters.component';

@Component({
  selector: 'filter-form',
  standalone: true,
  imports: [
    CommonModule,
    FilterSelectModule,
    ReactiveFormsModule,
    MoreFiltersComponent
  ],
  providers: [],
  templateUrl: './filter-form.component.html',
  styleUrls: ['./filter-form.component.scss']
})
export class FilterFormComponent implements OnInit, OnDestroy {
  // filtersChanged is the filter values converted to API params, which are number ids
  // formValuesChanged contains the string name/titles that are seen by the user
  @Output() filtersChanged = new EventEmitter<IAPIFilterOption[]>();
  @Output() filterRetrievalError = new EventEmitter<boolean>();
  @Output() formValuesChanged = new EventEmitter<Partial<IFilterFormValues>>();
  @Output() updateDynamicSeo = new EventEmitter<ISeoElements>();

  concernOptions: Array<IOptionCategory<IConcernOptionValue>> = [];
  genderOptions: Array<IOptionCategory<number>> = [];
  insuranceOptions: Array<IOptionCategory<IInsuranceOptionValue>> = [];
  stateOptions: Array<IOptionCategory<string>> = [];
  optionsLoaded$!: Observable<boolean>;

  filterFormGroup: FormGroup<IFilterForm> = new FormGroup({
    concerns: new FormControl<IConcernOptionValue[]>([], { nonNullable: true }),
    genders: new FormControl<number[]>([], { nonNullable: true }),
    insurance: new FormControl<IInsuranceOptionValue | null>(null),
    state: new FormControl<string | null>(null, Validators.required),
  });

  compareConcerns = compareConcernValues;
  compareInsurances = compareInsuranceValues;
  compareSimpleTypes = compareSimpleTypeOptionValues;

  destroyed$ = new EventEmitter<void>();
  clearSelectedFormOptions: Subject<void> = new Subject<void>();
  formChangeSubscription = new Subscription();
  filterFormLoaded: boolean = false;

  constructor(
    private filtersStore: FilterOptionsStore,
    private statesServedStore: StatesServedStore,
    private route: ActivatedRoute,
    private outgoingFiltersConversionService: OutgoingFilterConversionService,
    @Inject(LAUNCH_DARKLY_SERVICE)
    private launchDarklyService: LaunchDarklyService,
    private gtmDataLayerService: GtmDataLayerService
  ) {}

  ngOnDestroy(): void {
    this.destroyed$.next();
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((paramMap) => {
      if (paramMap.keys.length > 0) {
        const state = paramMap.get('state');
        if (state) {
          this.filtersStore.setCurrentState(state);
          this.gtmDataLayerService.sendProviderDirectoryInitialState(state, 'route');
        }
      }
    });

    this.optionsLoaded$ = this.filtersStore.isStateLoaded$().pipe(
      filter((isStateLoaded) => !!isStateLoaded),
      take(1),
      switchMap(() => combineLatest([
        this.getConcernOptions$(),
        this.getGenderOptions$(),
        this.getInsuranceOptions$(),
        this.getStateOptions$()
      ])),
      map(() => true)
    );

    combineLatest([
      this.route.queryParamMap,
      this.optionsLoaded$
    ]).subscribe(([paramMap, optionsLoaded]) => {
      // The ordering of these if blocks is important.
      if (!this.filterFormLoaded) {
        this.startFormChangeSubscription();
        this.startStateSubscription();
        if (paramMap.keys.length > 0) {
          const state = paramMap.get('state') ?? this.filtersStore.getCurrentState();

          this.filterFormGroup.setValue({
            concerns: this.buildConcernsFromQueryParams(paramMap),
            genders: this.buildGendersFromQueryParams(paramMap),
            insurance: this.buildInsuranceFromQueryParam(paramMap),
            state
          });
        }
      }

      if (optionsLoaded && !this.filterFormLoaded) {
        this.filterFormLoaded = true;
        this.filtersStore.setIsLoaded(true);
      }
    });
  }

  private getConcernOptions$(): Observable<boolean> {
    return combineLatest([
      this.filtersStore.specialtyList$(),
      this.filtersStore.modalityList$(),
    ]).pipe(
      filter(([specialties, modalities]) => specialties.length > 0 && modalities.length > 0),
      tap(([specialties, modalities]) => {
        const specialtiesSortedByTopSelection = specialties.sort(
          (s1, s2) => Number(s2.isTopSelection) - Number(s1.isTopSelection)
        );
        const specialtyOptions: Array<DropdownOption<IConcernOptionValue>> = specialtiesSortedByTopSelection.map(
          (item: ISpecialty) => (
            {
              value: {
                id: item.id, isTopSelection: item.isTopSelection, isModality: false, title: item.title
              },
              name: item.title
            }
          ));
        const modalityOptions: Array<DropdownOption<IConcernOptionValue>> = modalities.map(
          (item: IModality) => (
            {
              value: {
                id: item.id, isTopSelection: false, isModality: true, title: item.title
              },
              name: item.title
            }
          ));
        this.launchDarklyService.checkFlagEnabled$(LDBooleanFeatureFlags.PDP_PSYCHIATRY_FILTER)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((res) => {
            if (!res) return;
            const psychiatristOption: DropdownOption<IConcernOptionValue> = {
              name: 'Psychiatric & Med Mgmt',
              value: {
                id: -1,
                isTopSelection: false,
                isModality: false,
                isPsychiatrist: true,
                title: UrlQueryParams.PSYCHIATRIC
              },
            };
            modalityOptions.push(psychiatristOption);
          });

        this.concernOptions = [{
          name: 'Type of service',
          options: modalityOptions
        }, {
          name: 'Specialties',
          options: specialtyOptions,
          trimmedListCount: 16
        }];
      }),
      catchError(() => of([]).pipe(
        tap(() => { this.filterRetrievalError.emit(true) })
      )),
      map(() => true)
    );
  }

  private getGenderOptions$(): Observable<boolean> {
    return this.filtersStore.genderList$().pipe(
      filter((genders) => genders.length > 0),
      tap((array: IGender[]) => {
        this.genderOptions = [{
          name: 'Gender preferences',
          options: array.map(
            (arrayMember: IGender) => (
              { value: arrayMember.id, name: arrayMember.title }
            )
          )
        }];
      }),
      catchError(() => of().pipe(
        tap(() => { this.filterRetrievalError.emit(true) })
      )),
      map(() => true)
    );
  }

  private getInsuranceOptions$(): Observable<boolean> {
    return this.filtersStore.insuranceList$().pipe(
      tap((array: IInsurance[]) => {
        const insurance: IOptionCategory<IInsuranceOptionValue> = {
          name: InsuranceCategoryName.INSURANCE,
          options: []
        };
        const medicare: IOptionCategory<IInsuranceOptionValue> = {
          name: InsuranceCategoryName.MEDICARE,
          options: []
        };
        const eap: IOptionCategory<IInsuranceOptionValue> = {
          name: InsuranceCategoryName.EAP,
          options: []
        };

        const noInsuranceOptions: IOptionCategory<IInsuranceOptionValue> = {
          name: '',
          options: [{
            name: 'I\'ll pay out of pocket',
            value: CASH_PAY_OPTION_VALUE
          }, {
            name: 'I\'m not sure',
            value: {
              pncId: NoInsuranceOptionValue.NOT_SURE,
              title: 'not sure',
              type: InsuranceType.CASH,
              tag: ''
            }
          }]
        };

        array.forEach((arrayMember: IInsurance) => {
          // Note: one "arrayMember" may belong to multiple categories.
          if (arrayMember.isMedicare) {
            medicare.options.push({
              value: {
                pncId: arrayMember.pncId,
                title: arrayMember.title,
                type: InsuranceType.MEDICARE,
                tag: arrayMember.tag
              },
              name: arrayMember.title
            });
          }

          if (arrayMember.isEap) {
            eap.options.push({
              value: {
                pncId: arrayMember.pncId,
                title: arrayMember.title,
                type: InsuranceType.EAP,
                tag: arrayMember.tag
              },
              name: arrayMember.title
            });
          }

          if (arrayMember.isPrivateInsurance) {
            insurance.options.push({
              value: {
                pncId: arrayMember.pncId,
                title: arrayMember.title,
                type: InsuranceType.INSURANCE,
                tag: arrayMember.tag
              },
              name: arrayMember.title
            });
          }
        });

        this.insuranceOptions = [noInsuranceOptions, medicare, insurance, eap];
      }),
      catchError(() => of().pipe(
        tap(() => { this.filterRetrievalError.emit(true) })
      )),
      map(() => true)
    );
  }

  private getStateOptions$(): Observable<boolean> {
    return this.statesServedStore.stateList$().pipe(
      tap((array: IState[]) => {
        this.stateOptions = [{
          name: 'My location',
          options: array.map(
            (arrayMember: IState) => (
              { value: arrayMember.abbreviation, name: arrayMember.name }
            )
          )
        }];
      }),
      catchError(() => of().pipe(
        tap(() => { this.filterRetrievalError.emit(true) })
      )),
      map(() => true)
    );
  }

  buildGendersFromQueryParams(queryParams: ParamMap): number[] {
    // get the gender param -- should be a number or comma separated list of numbers
    const gendersParam = queryParams.get(UrlQueryParams.GENDERS);
    // if it doesn't exist, return the array
    if (!gendersParam){
      return [];
    }

    const genderStrings = gendersParam.split(',');
    const genderValues: number[] = [];
    genderStrings.forEach((genderParam: string) => {
      const gender = this.genderOptions[0].options.find((option) => option.name === genderParam);

      if (gender) {
        genderValues.push(gender.value);
      }
    });
    return genderValues;
  }

  private buildInsuranceFromQueryParam(queryParams: ParamMap): IInsuranceOptionValue | null {
    const insuranceNameParam = queryParams.get(UrlQueryParams.INSURANCE);
    if (!insuranceNameParam) {
      return null;
    }
    let foundOption: IInsuranceOptionValue | null = null;
    this.insuranceOptions.forEach((category) => {
      category.options.forEach((option) => {
        if (option.value.title === insuranceNameParam) {
          foundOption = option.value;
        }
      });
    });

    return foundOption;
  }

  /**
   * Builds 'concerns' from route parameters, combining specialties and modalities.
   * @param { ParamMap } - route parameters
   * @returns an array of specialty and modality ids
   */
  buildConcernsFromQueryParams(params: ParamMap): IConcernOptionValue[] {
    let results: IConcernOptionValue[] = [];

    const specialtiesParams: string | null = params.get(UrlQueryParams.SPECIALTIES);

    if (specialtiesParams) {
      const specialtiesArray = specialtiesParams.split(',');
      const specialties: IConcernOptionValue[] = [];
      specialtiesArray.forEach((specialityTitle) => {
        const specialtyOptions = this.concernOptions[1].options;
        const option = specialtyOptions.find((opt) => opt.value.title === specialityTitle);
        if (option) {
          specialties.push(option.value);
        }
      });
      results = results.concat(specialties);
    }

    const modalitiesParams: string | null = params.get(UrlQueryParams.MODALITIES);

    if (modalitiesParams) {
      const modalitiesArray = modalitiesParams.split(',');
      const modalities: IConcernOptionValue[] = [];
      modalitiesArray.forEach((modalityTitle) => {
        const modalityOptions = this.concernOptions[0].options;
        const option = modalityOptions.find((opt) => opt.value.title === modalityTitle);
        if (option) {
          modalities.push(option.value);
        }
      });
      results = results.concat(modalities);
    }

    return results;
  }

  handleMoreFilterChange(selections: IMoreFiltersSelections): void {
    this.pauseFormChangeSubscription();

    this.filterFormGroup.controls.genders.setValue(selections.genderSelections);
    this.filterFormGroup.controls.insurance.setValue(selections.insuranceSelection);
    this.filterFormGroup.controls.state.setValue(selections.locationSelection);
    // emit outgoing filters
    const formValues = this.filterFormGroup.getRawValue();
    const outgoingFilters = this.outgoingFiltersConversionService.formFiltersToApiFilterValues(formValues);
    this.emitChanges(outgoingFilters);

    this.startFormChangeSubscription();
  }

  private pauseFormChangeSubscription(): void {
    this.formChangeSubscription.unsubscribe();
  }

  private startStateSubscription(): void {
    this.filtersStore.currentState$().pipe(
      // Don't react to these changes if we already have a value.
      filter(() => !this.filterFormGroup.controls.state.value),
      takeUntil(this.destroyed$)
    ).subscribe((state) => {
      if (state) {
        this.filterFormGroup.controls.state.setValue(state);
      }
    });
  }

  private startFormChangeSubscription(): void {
    this.formChangeSubscription = this.filterFormGroup.valueChanges.pipe(
      map((values) => {
        this.updateDynamicSeo.emit({ filterFormValues: this.filterFormGroup.getRawValue(), genderOptions: this.genderOptions });
        // Since insurances are based on location, update the insurance options when the state changes
        // and reset any previous insurance selected since it is no longer a valid option in the new state
        const stateChange = values.state;
        if (stateChange && stateChange !== this.filtersStore.getCurrentState()) {
          this.filtersStore.setCurrentState(stateChange);
          return this.getInsuranceOptions$();
        }
        return of(false);
      }),
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      // emit outgoing filters
      const formValues = this.filterFormGroup.getRawValue();
      const outgoingFilters = this.outgoingFiltersConversionService.formFiltersToApiFilterValues(formValues);
      this.emitChanges(outgoingFilters);
    });
  }

  private async emitChanges(changes: IAPIFilterOption[]): Promise<void> {
    this.filtersChanged.emit(changes);
    this.formValuesChanged.emit(this.filterFormGroup.value);
  }
}
